import { useState, useEffect } from 'react';
import {
  fetchEmployeeById,
  updateEmployeeById,
  deleteEmployee,
  checkUsernameExists,
} from '../services/employeeService';
import { validateFields } from '../utils/employeeModalUtils';
import { FieldsList } from '../config/employeeFieldsConfig';

const useEmployeeForm = (employeeId, handleClose) => {
  const [formData, setFormData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalUsername, setOriginalUsername] = useState(''); // Store original username

  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId) return;
      try {
        const data = await fetchEmployeeById(employeeId);
        setFormData(data);
        setOriginalUsername(data.username); // Set original username
      } catch (error) {
        console.error('Error fetching employee:', error);
      }
    };
    fetchData();
  }, [employeeId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    const isAddMode = !employeeId;

    // Validate fields
    const errors = validateFields(formData, FieldsList, isAddMode);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Check if the username already exists when editing and has changed
    if (formData.username && formData.username !== originalUsername) {
      try {
        const usernameExists = await checkUsernameExists(formData.username);
        if (usernameExists) {
          // Add a validation error for the username field
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            username: 'Username already exists. Please enter a unique username.',
          }));
          return false;
        }
      } catch (error) {
        console.error('Error checking username uniqueness:', error);
        alert('An error occurred while checking username uniqueness.');
        return false;
      }
    }

    // Proceed with updating the employee
    try {
      await updateEmployeeById(employeeId, formData);
      alert('Employee updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating employee:', error);
      alert('An error occurred while updating employee');
      return false;
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm('Are you sure you want to delete this employee?');
    if (confirmDelete) {
      try {
        await deleteEmployee(employeeId);
        alert('Employee deleted successfully');
        handleClose();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('An error occurred while deleting the employee');
      }
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const setEditModeFalse = () => {
    setIsEditMode(false);
  };

  return {
    formData,
    isEditMode,
    validationErrors,
    handleInputChange,
    handleSubmit,
    toggleEditMode,
    setEditModeFalse,
    handleDelete,
  };
};

export default useEmployeeForm;
